import { useQuery } from '@tanstack/react-query';
import { Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { GenerationLogRow } from '../../api/types';
import { DateText } from '../../lib/format';
import { fetchAbonamente, fetchGenerationLog } from './api';

const { Text } = Typography;
const PAGE_SIZE = 20;
/** A firm realistically has a handful of subscriptions, never a list worth
 * paging through — same pragmatic cap as `FacturaEditorPage`'s products
 * page (100 is also the server's hard `paginationQuerySchema` cap). */
const SUBSCRIPTIONS_PAGE_SIZE = 100;

/**
 * `Tab: Istoric generări` (task-8d-brief.md §2) — `GET /generation-log`.
 *
 * That endpoint has NO `companyId` filter of its own (it only accepts an
 * optional single `subscriptionId` — see `generation-log.controller.ts`'s
 * own doc comment; adding one is backend work this task isn't allowed to
 * do). To avoid showing another firm's subscription names in the table, the
 * CURRENT firm's own subscription ids are loaded once and used to narrow the
 * server's response client-side when no specific subscription is selected —
 * same "derived filter, current-page-only" pattern `FacturiListPage`'s
 * STORNOED segment already uses, with the same caveat surfaced to the user.
 * Selecting one specific subscription (always drawn from THIS firm's own
 * list) needs no such narrowing — the server already scopes to it exactly.
 */
export function GenerationLogTab({ firmaId }: { firmaId: string }) {
  const [page, setPage] = useState(1);
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>();
  const [status, setStatus] = useState<'SUCCESS' | 'FAILED' | undefined>();

  const { data: subscriptionsPage } = useQuery({
    queryKey: ['subscriptions', firmaId, 'all-for-log-filter'],
    queryFn: () => fetchAbonamente({ companyId: firmaId, page: 1, pageSize: SUBSCRIPTIONS_PAGE_SIZE }),
    enabled: !!firmaId,
  });
  const firmSubscriptions = subscriptionsPage?.items ?? [];
  const firmSubscriptionIds = useMemo(() => new Set(firmSubscriptions.map((s) => s.id)), [firmSubscriptions]);
  const subscriptionOptions = firmSubscriptions.map((s) => ({ value: s.id, label: s.name }));

  const { data, isLoading } = useQuery({
    queryKey: ['generation-log', { page, subscriptionId, status }],
    queryFn: () => fetchGenerationLog({ page, pageSize: PAGE_SIZE, subscriptionId, status }),
    enabled: !!firmaId,
  });

  const rows = useMemo(() => {
    const items = data?.items ?? [];
    return subscriptionId ? items : items.filter((row) => firmSubscriptionIds.has(row.subscriptionId));
  }, [data, subscriptionId, firmSubscriptionIds]);

  const columns: ColumnsType<GenerationLogRow> = [
    { title: 'Dată', key: 'executedAt', width: 160, render: (_, row) => <DateText value={row.executedAt} /> },
    { title: 'Abonament', dataIndex: 'subscriptionName' },
    { title: 'Perioadă', dataIndex: 'periodKey', width: 120 },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, row) =>
        row.status === 'SUCCESS' ? <Tag color="green">Succes</Tag> : <Tag color="red">Eșec</Tag>,
    },
    {
      title: 'Factură',
      key: 'invoice',
      width: 130,
      render: (_, row) =>
        row.invoiceId ? <Link to={`/facturi/${row.invoiceId}`}>Vezi factura</Link> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Eroare',
      key: 'error',
      render: (_, row) =>
        row.errorMessage ? (
          <Tooltip title={row.errorMessage}>
            <Text type="danger" ellipsis style={{ maxWidth: 280, display: 'inline-block' }}>
              {row.errorMessage}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 260 }}
          placeholder="Toate abonamentele firmei"
          value={subscriptionId}
          onChange={(value: string | undefined) => {
            setSubscriptionId(value);
            setPage(1);
          }}
          options={subscriptionOptions}
        />
        <Select
          allowClear
          style={{ width: 160 }}
          placeholder="Toate statusurile"
          value={status}
          onChange={(value: 'SUCCESS' | 'FAILED' | undefined) => {
            setStatus(value);
            setPage(1);
          }}
          options={[
            { value: 'SUCCESS', label: 'Succes' },
            { value: 'FAILED', label: 'Eșec' },
          ]}
        />
      </Space>

      {!subscriptionId ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Se afișează doar generările abonamentelor firmei curente, pe pagina curentă — paginarea numără toate
          abonamentele din sistem. Alege un abonament pentru istoricul lui complet.
        </Text>
      ) : null}

      <Table<GenerationLogRow>
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.total ?? 0,
          onChange: setPage,
          showSizeChanger: false,
        }}
      />
    </div>
  );
}
